import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import i18next from 'i18next';
import _includes from 'lodash/includes';
import _head from 'lodash/head';
import _replace from 'lodash/replace';
import _toUpper from 'lodash/toUpper';
import _split from 'lodash/split';
import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
import _castArray from 'lodash/castArray';
import { getValueFromSessionStorage, setValueInSessionStorage, removeValueFromSessionStorage } from '@tekion/tekion-base/utils/sessionStorage';
import { getValueFromLocalStorage, setValueInLocalStorage } from '@tekion/tekion-base/utils/localStorage';
import DealerInfo from 'tbase/readers/DealerInfo';
import WorkspaceReader from 'tbase/readers/workspaceInfo';
import UserReader from 'tbase/readers/User';
import TEnvReader from 'tbase/readers/Env';
import TRANSLATIONS_LS_CACHE_KEYS from '@tekion/tekion-base/constants/translationsLSCacheKeys';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '@tekion/tekion-base/app.constants';
import { HTTP_CODES } from '@tekion/tekion-base/utils/validation'; // Constants

import LOCALES from '@tekion/tekion-base/constants/locales';
import canUseDOM from './can-use-dom';
import LANGUAGE_CONFIG, { SUPPORTED_LANUGAGE, DEFAULT_LANGUAGE } from '../language.config';
/**
 * Sends the current Local langugage set in i18next Library,
 * When i18n is configured for the first time , we might get unexpected languge
 * which we dont have support for.
 *
 * @returns string One of the SUPPORTED_LANUGAGE
 */

export var getCurrentLanguge = function getCurrentLanguge() {
  var language = i18next.language;
  if (isLanguageExist(language)) return language;
  return DEFAULT_LANGUAGE;
};
export var getAdaptedLocale = function getAdaptedLocale(locale) {
  return _toUpper(_replace(locale, '-', '_'));
};
/**
 * Checks if we support the currnt language or not.
 * @param {string} language
 */

export var isLanguageExist = function isLanguageExist(language) {
  return _includes(SUPPORTED_LANUGAGE, language);
};
export var getLanguageConfig = function getLanguageConfig() {
  var code = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getCurrentLanguge();
  return LANGUAGE_CONFIG[code];
};
export var getCurrencyCode = function getCurrencyCode() {
  var code = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getCurrentLanguge();
  return getLanguageConfig(code).currency.code;
};
export var rebind = function rebind(i18n) {
  if (canUseDOM) window.__ = i18n.t.bind(i18n);
};
export var geti18nConfig = function geti18nConfig(namespace, locale, fetchApi) {
  if (!namespace) {
    throw Error('Namespace empty! Not able to load translations');
  }

  return {
    debug: false,
    lng: getAdaptedLocale(locale),
    // this will ensure key fallback is in place
    // https://www.i18next.com/principles/fallback#key-not-found
    fallbackLng: false,
    returnEmptyString: false,
    backend: {
      loadPath: function loadPath() {
        return '{{lng}}|{{ns}}';
      },
      request: function () {
        var _request = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_options, _url, _payload, cb) {
          var _split2, _split3, language, nm, translationData;

          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _split2 = _split(_url, '|'), _split3 = _slicedToArray(_split2, 2), language = _split3[0], nm = _split3[1];
                  _context.prev = 1;
                  _context.next = 4;
                  return fetchApi(nm || namespace, language);

                case 4:
                  translationData = _context.sent;
                  cb(null, {
                    status: HTTP_CODES.OK,
                    data: translationData
                  });
                  _context.next = 12;
                  break;

                case 8:
                  _context.prev = 8;
                  _context.t0 = _context["catch"](1);
                  console.error(_context.t0);
                  cb(null, {
                    status: HTTP_CODES.OK,
                    data: EMPTY_OBJECT
                  }); // prevent retry

                case 12:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, null, [[1, 8]]);
        }));

        function request(_x, _x2, _x3, _x4) {
          return _request.apply(this, arguments);
        }

        return request;
      }()
    },
    detection: {
      lookupQuerystring: 'language'
    },
    ns: [namespace],
    nsSeparator: false,
    keySeparator: false,
    defaultNS: namespace,
    react: {
      wait: true,
      useSuspense: true
    },
    interpolation: {
      escapeValue: false
    }
  };
}; // user can change the current locale on-demand.
// this overrides the user locale preference.

export var changeCurrentLocale = function changeCurrentLocale(locale) {
  var currentLocale = getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE);
  if (currentLocale === locale) return;
  setValueInSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE, locale);
  setValueInLocalStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE, locale);

  if (canUseDOM) {
    i18next.changeLanguage(getAdaptedLocale(locale));
  }
};
export var getI18nInstance = function getI18nInstance() {
  return i18next;
};
export var changePreferredLocale = function changePreferredLocale(locale) {
  var preferredLocale = getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.PREFERRED_LOCALE); // set user preferred locale only once, after user logs in.
  // if user preference changes, user should log out and log in.

  if (_isNil(preferredLocale)) setValueInSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.PREFERRED_LOCALE, locale);
  changeCurrentLocale(locale);
};
export var getDealerSupportedLocale = function getDealerSupportedLocale() {
  var supportedLocale = EMPTY_ARRAY;
  var supportedLocaleFromLocalStorage = getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.SUPPORTED_LOCALE);

  try {
    supportedLocale = JSON.parse(supportedLocaleFromLocalStorage);
  } catch (_unused) {
    supportedLocale = _castArray(supportedLocaleFromLocalStorage);
  }

  return supportedLocale;
};
export var setDealerSupportedLocale = function setDealerSupportedLocale(dealerConfig) {
  var dealerSupportedLanguages = DealerInfo.supportedLanguages(dealerConfig) || EMPTY_ARRAY;
  var currentSupportedLocale = getDealerSupportedLocale();
  if (!_isEqual(dealerSupportedLanguages, currentSupportedLocale)) setValueInSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.SUPPORTED_LOCALE, JSON.stringify(dealerSupportedLanguages));
};
export var getLocaleToDisplay = function getLocaleToDisplay(dealerConfig) {
  var dealerSupportedLanguages = DealerInfo.supportedLanguages(dealerConfig);
  var currentActiveLocale = getValueFromLocalStorage('currentActiveLocale');
  var userPreferredLocale = getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE) || currentActiveLocale || UserReader.language(TEnvReader.userInfo()) || getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.PREFERRED_LOCALE); // if user preferred locale is no longer supported by dealer, we should fallback to dealer default.

  var localeToUse = _includes(dealerSupportedLanguages, userPreferredLocale) ? userPreferredLocale : getDealerLocale(dealerConfig);

  if (_isNil(currentActiveLocale) && document.visibilityState === 'visible') {
    setValueInLocalStorage('currentActiveLocale', localeToUse);
  }

  return localeToUse;
};
export var getDealerLocale = function getDealerLocale(dealerConfig) {
  return DealerInfo.dealerLanguage(dealerConfig) || LOCALES.EN_US;
};
export var getWorkspaceLocaleToDisplay = function getWorkspaceLocaleToDisplay(dealerConfig) {
  var dealerSupportedLanguages = WorkspaceReader.supportedLanguages(dealerConfig);
  var userPreferredLocale = getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE) || UserReader.language(TEnvReader.userInfo()) || getValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.PREFERRED_LOCALE); // if user preferred locale is no longer supported by dealer, we should fallback to dealer default.

  return _includes(dealerSupportedLanguages, userPreferredLocale) ? userPreferredLocale : WorkspaceReader.language(dealerConfig);
};
export var clearApplicationTranslationKeys = function clearApplicationTranslationKeys() {
  removeValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.CURRENT_LOCALE);
  removeValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.PREFERRED_LOCALE);
  removeValueFromSessionStorage(TRANSLATIONS_LS_CACHE_KEYS.SUPPORTED_LOCALE);
};